// @flow

import React, {Fragment} from 'react';
import styled from 'styled-components';
import {CanvasManager} from './managers/canvas-manager';
import type {ActionButtonType, EnterStateActionButtonType} from './types';
import {ColorPalette} from './helpers/BieColorPalette';
import {BieShapeSelect} from './helpers/BieShapeSelect';

type TooltipButtonProps = {
  title: string,
  icon: string,
  onClick: (ActionButtonType) => any,
};

const TooltipButton = ({
  title,
  icon,
  onClick,
  ...other
}: TooltipButtonProps) => (
  <div className="btn-container">
    <div className="editor-tooltip">{title}</div>
    <button {...other} className="btn" onClick={onClick}>
      <i className={`${icon}`} />
    </button>
  </div>
);

type ActionTooltipButtonProps = {
  action: ActionButtonType,
  onClick: (ActionButtonType) => any,
};

const ActionTooltipButton = ({
  action,
  onClick,
  ...other
}: ActionTooltipButtonProps) => (
  <TooltipButton
    {...other}
    title={action.title}
    icon={action.icon}
    onClick={() => onClick(action)}
  />
);

const StyledToolbarDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.75);

  .divider {
    margin: 4px;
    border-left: 1px solid #ccc;
  }

  .btn-container {
    position: relative;

    .editor-tooltip {
      display: none;
      position: absolute;
      top: -25px;
      font-size: 9pt;
      background: rgba(0, 0, 0, 0.75);
      color: #fff;
      padding: 4px;
      line-height: 1;
      left: 50%;
      transform: translateX(-50%);
      white-space: nowrap;
    }

    &:hover .editor-tooltip,
    &:focus .editor-tooltip {
      display: block;
    }
  }

  .btn {
    padding: 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: #222222;
    border: 0;
    cursor: pointer;
    transition: background 0.25s;
    user-select: none;
    font-size: 1em;
    width: 38px;
    height: 38px;

    &:focus {
      outline: none;
    }

    &:not([disabled]):hover {
      background: #f0f0f0;
    }

    &[disabled] {
      opacity: 0.25;
    }
  }

  .color-button-active {
    border: 5px;
    border-style: solid;
    border-color: pink;
  }
`;

type Props = {
  actionButtons: Array<ActionButtonType>,
  canvasManager: CanvasManager,
  onClose: () => void,
  onSave: (any) => void,
};

type State = {
  activeAction: EnterStateActionButtonType | null,
};

export class BieToolbar extends React.Component<Props, State> {
  state = {activeAction: null};

  handleButtonClick = (action: ActionButtonType) => {
    const {canvasManager} = this.props;

    switch (action.type) {
      case 'immediate-action': {
        action.cb(canvasManager);
        return this.forceUpdate();
      }
      case 'enter-state': {
        action.enter(canvasManager);
        return this.setState({activeAction: action});
      }
      default: {
        return this.forceUpdate();
      }
    }
  };

  undo = () => {
    this.props.canvasManager.undo();
    this.forceUpdate();
  };

  redo = () => {
    this.props.canvasManager.redo();
    this.forceUpdate();
  };

  handleCancelClick = () => {
    const {activeAction} = this.state;
    const {canvasManager, onClose} = this.props;

    if (activeAction != null) {
      activeAction.cancel(canvasManager);
      this.setState({activeAction: null});
    } else {
      onClose();
    }
  };

  handleConfirmClick = () => {
    const {activeAction} = this.state;
    const {canvasManager, onSave} = this.props;

    if (activeAction != null) {
      activeAction.confirm(canvasManager);
      this.setState({activeAction: null});
    } else {
      onSave();
    }
  };

  handleEventClick = (event: string) => {
    const {activeAction} = this.state;
    const {canvasManager} = this.props;
    if (activeAction != null && activeAction.triggerEvent != null) {
      activeAction.triggerEvent(canvasManager, event);
    }
  };

  render() {
    const {actionButtons, canvasManager} = this.props;
    const {activeAction} = this.state;

    const canUndo = canvasManager.stateManager.canUndo();
    const canRedo = canvasManager.stateManager.canRedo();

    return (
      <StyledToolbarDiv className="content" {...this.props}>
        {activeAction == null ? (
          <Fragment>
            {actionButtons.map((action) => (
              <ActionTooltipButton
                key={action.slug}
                action={action}
                onClick={this.handleButtonClick}
              />
            ))}
            <div className="divider" />

            <TooltipButton
              title={'Undo'}
              icon="fas fa-undo"
              disabled={!canUndo}
              onClick={this.undo}
            />

            <TooltipButton
              title={'Redo'}
              icon="fas fa-redo"
              disabled={!canRedo}
              onClick={this.redo}
            />

            {activeAction != null ? <activeAction.ActionComponent /> : null}

            <div className="divider" />

            <TooltipButton
              title={'Cancel'}
              icon="fas fa-times text-danger"
              onClick={this.handleCancelClick}
            />
          </Fragment>
        ) : (
          <TooltipButton
            title={'Discard changes'}
            icon="fas fa-arrow-left text-danger"
            onClick={this.handleCancelClick}
          />
        )}

        {activeAction != null &&
          activeAction.eventButtons != null &&
          activeAction.eventButtons.map((eventButton, index) => {
            if (eventButton.type === 'colorPalette') {
              return (
                <ColorPalette
                  key={'colorPalette-object-' + index}
                  handleEventClick={this.handleEventClick}
                />
              );
            } else if (eventButton.type === 'shapeSelect') {
              return (
                <BieShapeSelect
                  key={'shapeSelect-object-' + index}
                  handleEventClick={this.handleEventClick}
                />
              );
            } else {
              return (
                <TooltipButton
                  key={'eventButton-' + eventButton.title}
                  title={eventButton.title}
                  icon={eventButton.icon}
                  onClick={() => this.handleEventClick(eventButton.eventType)}
                  style={eventButton.style}
                />
              );
            }
          })}

        <TooltipButton
          title={'Apply changes'}
          icon="fas fa-check text-success"
          onClick={this.handleConfirmClick}
        />
      </StyledToolbarDiv>
    );
  }
}
